import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { createQueryClient, refreshSession } from './api';
import { AppRouter } from './routes/AppRouter';

/**
 * The app is its routes (`CR-006`), plus the one question that has to be asked before any of them can be
 * answered: is this person signed in?
 *
 * A reload starts with no access token — the refresh token is in an `HttpOnly` cookie this code cannot
 * read (`ARCH-003 §1.1`) — so the answer takes one request. `refreshSession` asks it, and until it
 * answers the session is `restoring` and a protected route renders nothing rather than bouncing somebody
 * to the welcome screen and back.
 *
 * It runs once. `refreshSession` is single-flight and latches on refusal, so a second call would be free
 * anyway, but "once on mount" is what this means and the empty dependency list says so.
 *
 * The query client is created in state rather than at module scope. At module scope it would be shared by
 * every test in a file — one test's cached ledger answering another's assertion — and in the browser it
 * would survive a hot reload holding data from the previous version of the code.
 */
export function App(): React.JSX.Element {
  const [queryClient] = useState(createQueryClient);

  useEffect(() => {
    void refreshSession();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppRouter />
    </QueryClientProvider>
  );
}
